import React, { useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { BaseButton } from '../buttons/BaseButton';

const SignatureCanvas = forwardRef(({
  width = 600,
  height = 180,
  initialLines = [],
  lineColor = '#000000',
  lineWidth = 2,
  onChange,
  disabled = false
}, ref) => {
  const canvasRef = useRef(null);

  useImperativeHandle(ref, () => ({
    getImageData: () => canvasRef.current?.toDataURL('image/png') || null
  }));
  const drawingRef = useRef(false);

  const [lines, setLines] = useState([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    lines.forEach(line => {
      if (!line.points?.length) return;

      ctx.strokeStyle = line.color;
      ctx.lineWidth = line.width;

      ctx.beginPath();
      line.points.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.stroke();
    });
  }, [lines, width, height]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    if (disabled) return;

    drawingRef.current = true;
    const pos = getPos(e);

    setLines(prev => [
      ...prev,
      {
        color: lineColor,
        width: lineWidth,
        points: [pos]
      }
    ]);
  };

  const draw = (e) => {
    if (disabled || !drawingRef.current) return;

    const pos = getPos(e);

    setLines(prev => {
      const updated = [...prev];
      updated[updated.length - 1].points.push(pos);
      return updated;
    });
  };

  const stopDrawing = () => {
    if (disabled || !drawingRef.current) return;

    drawingRef.current = false;

    if (onChange) onChange(lines);
  };

  const clear = () => {
    setLines([]);
    if (onChange) onChange([]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          border: '1px solid #d9d9d9',
          borderRadius: 4,
          background: '#fff',
          touchAction: 'none',
          maxWidth: '100%'
        }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />

      {!disabled && (
        <BaseButton
          style={{ width: 'auto', alignSelf: 'flex-start' }}
          type="default"
          htmlType="button"
          size="default"
          text="Limpar"
          onClick={clear}
        />
      )}
    </div>
  );
});

SignatureCanvas.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  initialLines: PropTypes.array,
  lineColor: PropTypes.string,
  lineWidth: PropTypes.number,
  onChange: PropTypes.func,
  disabled: PropTypes.bool
};

export default SignatureCanvas;
